import {
  ApplicationRef,
  Directive,
  ElementRef,
  HostListener,
  Input,
  NgZone,
  OnDestroy,
  OnInit,
  TemplateRef,
} from '@angular/core';
import { createPopper, flip, Instance, preventOverflow } from '@popperjs/core';

@Directive({
  selector: '[libPopover]',
  exportAs: 'lib-popover',
  standalone: false,
})
export class PopoverDirective implements OnInit, OnDestroy {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  @Input() libPopover: TemplateRef<any>;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  @Input() popoverContext: any;

  @Input() placement: 'auto' | 'top' | 'bottom' | 'left' | 'right' = 'top';

  @Input() triggers = 'click';

  @Input() containerClass = '';

  @Input() outsideClick: boolean;

  @Input() arrow = true;

  private popoverRef: HTMLElement;
  private popperInstance: Instance;

  private persistIsShowing: boolean;

  constructor(
    private el: ElementRef,
    private applicationRef: ApplicationRef,
    private zone: NgZone,
  ) {}

  ngOnInit() {
    if (this.triggers.includes('hover')) {
      this.el.nativeElement.addEventListener(
        'mouseenter',
        this.mouseenterEvent,
      );

      this.el.nativeElement.addEventListener(
        'mouseleave',
        this.mouseleaveEvent,
      );
    } else if (this.triggers.includes('click')) {
      this.el.nativeElement.addEventListener('click', this.clickEvent);
    }
  }

  ngOnDestroy() {
    this.hidePopover();

    if (this.triggers.includes('hover')) {
      this.el.nativeElement.removeEventListener(
        'mouseenter',
        this.mouseenterEvent,
      );

      this.el.nativeElement.removeEventListener(
        'mouseleave',
        this.mouseleaveEvent,
      );
    } else if (this.triggers.includes('click')) {
      this.el.nativeElement.removeEventListener('click', this.clickEvent);
    }
  }

  private showPopover() {
    const viewRef = this.libPopover.createEmbeddedView(this.popoverContext);
    this.applicationRef?.attachView(viewRef);

    this.popoverRef = document.createElement('div');
    this.popoverRef.className = this.containerClass
      ? `bs-popover-auto popover ${this.containerClass}`
      : 'bs-popover-auto popover';
    this.popoverRef.style.zIndex = '1070';

    const popperOptions = {
      modifiers: [flip, preventOverflow],
      placement: this.placement,
    };
    if (this.arrow) {
      const arrow = document.createElement('div');
      arrow.className = 'popover-arrow';
      this.popoverRef.append(arrow);

      for (const node of viewRef.rootNodes) {
        this.popoverRef.append(node);
      }

      popperOptions.modifiers.push(
        {
          name: 'arrow',
          options: {
            element: arrow,
          },
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
        } as any,
        {
          name: 'offset',
          options: {
            offset: [0, 8],
          },
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
        } as any,
      );
    }

    /*
     * Initial position wrong if not in a timeout?!
     */
    this.zone.runOutsideAngular(() => {
      setTimeout(() => {
        document.body.append(this.popoverRef);

        this.popperInstance = createPopper(
          this.el.nativeElement,
          this.popoverRef,
          popperOptions,
        );

        this.popperInstance.update();
      });
    });

    if (this.triggers.includes('hover')) {
      this.popoverRef.addEventListener('mouseleave', this.mouseleaveEvent);

      this.popoverRef.addEventListener('mouseenter', this.mouseenterEvent);
    }
  }

  private hidePopover() {
    if (!this.popoverRef) {
      return;
    }
    this.popoverRef.remove();
    this.popoverRef = undefined;
    this.popperInstance.destroy();
    this.popperInstance = undefined;
  }

  show() {
    if (!this.popoverRef) {
      this.showPopover();
    }
  }

  hide() {
    if (this.popoverRef) {
      this.hidePopover();
    }
  }

  private persistHidePopover() {
    this.persistIsShowing = false;
    this.zone.runOutsideAngular(() => {
      setTimeout(() => {
        if (!this.persistIsShowing) {
          this.hide();
        }
      }, 250);
    });
  }

  private persistShowPopover() {
    this.persistIsShowing = true;
    this.show();
  }

  @HostListener('document:mousedown', ['$event.target'])
  public checkOutsideClick(targetElement) {
    if (!this.popoverRef) {
      return;
    }

    const clickedInside =
      this.popoverRef.contains(targetElement) ||
      this.el.nativeElement === targetElement ||
      this.el.nativeElement.contains(targetElement);
    if (!clickedInside) {
      this.hidePopover();
    }
  }

  private mouseenterEvent = () => {
    this.persistShowPopover();
  };

  private mouseleaveEvent = () => {
    this.persistHidePopover();
  };

  private clickEvent = () => {
    if (this.popoverRef) {
      this.hidePopover();
    } else {
      this.showPopover();
    }
  };
}
